import React from 'react';
import { Box, Chip, Divider } from '@mui/material';
import { useResizeObserver, useScrollableView } from '../../Layout';
import { useUserMessagesQuery } from '@uplift/queries';
import { useCurrentuser, useTimezone } from '@uplift/contexts';
import { Color } from '../types';
import { MessageBubble } from './MessageBubble.component';
import { Loader } from '../../Loader';
import { CalendarDateService } from '@uplift/services';
import { DateFormats } from '@uplift/constants';
import { Message } from '@uplift/api';


export type MessagesContainerProps = {
  conversationId: string;
  color: Color;
};

const MessagesGroup: React.FC<{ color: Color; group: { date: string; messages: Message[] } }> = ({ color, group }) => {
  const me = useCurrentuser();
  const rootRef = React.useRef<HTMLDivElement>(null);

  const groupLabel = React.useMemo(() => {
    const date = CalendarDateService.date(group.date, DateFormats.ISO_DATE);

    if (date.isSame(CalendarDateService.date(), 'date')) {
      return 'Today';
    }

    return CalendarDateService.format(date, 'dddd, MMMM D', 'dddd, MMMM D, YYYY');
  }, [ group.date ]);

  return (
    <Box
      ref={rootRef}
      position="relative"
      data-testid="messages-group"
    >
      <Box sx={{
        position: 'absolute',
        width: '100%',
        top: 0,
        height: '51px',
        bgcolor: group.messages[0]?.seenAt ? 'transparent' : 'grey.50',
      }}
      >
        <Divider sx={{
          position: 'absolute',
          top: '25px',
          width: '100%',
        }}
        />
      </Box>
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '51px',
        }}
      >
        <Chip
          sx={{
            bgcolor: 'white',

            '&.MuiChip-root:hover': {
              bgcolor: 'white',
              cursor: 'pointer',
            },
          }}
          variant="outlined"
          label={groupLabel}
          data-testid="messages-group-title"
          onClick={() => {
            rootRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
        />
      </Box>
      {group.messages.map((message) => (
        <MessageBubble
          key={message.id}
          message={message}
          color={color}
          direction={message.senderId === me.id ? 'send' : 'receive'}
        />
      ))}
    </Box>
  );
};


export const MessagesContainer: React.FC<MessagesContainerProps> = ({ conversationId, color }) => {
  const me = useCurrentuser();
  const scrollableView = useScrollableView();
  const [ sticked, setSticked ] = React.useState(true);
  const scrollContainer = scrollableView.scrollContainer;
  const [ body, setBody ] = React.useState<HTMLDivElement | null>(null);
  const { data: messages = [], isLoading, isFetchedAfterMount } = useUserMessagesQuery(conversationId);
  const lastMessage = messages[messages.length - 1];

  React.useEffect(() => {
    if (!scrollContainer || !isFetchedAfterMount) return;
    const oldestUnreadMessage = messages.find((message) => !message.seenAt && message.senderId !== me.id);

    if (!oldestUnreadMessage) return;
    scrollContainer.querySelector(`[data-message-id="${oldestUnreadMessage.id}"]`)?.scrollIntoView({ block: 'start' });
  }, [ isFetchedAfterMount ]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!scrollContainer || lastMessage?.senderId !== me.id) return;

    scrollContainer.scrollTo({
      top: scrollContainer.scrollHeight,
      behavior: 'smooth',
    });
  }, [ lastMessage?.message ]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (!scrollContainer) return;

    const scrollHandler = () => {
      if (!scrollContainer) return;
      if (scrollContainer.clientHeight === Math.floor(scrollContainer.scrollHeight - scrollContainer.scrollTop)) {
        setSticked(true);
      } else {
        setSticked(false);
      }
    };

    scrollContainer.addEventListener('scroll', scrollHandler);

    return () => scrollContainer.removeEventListener('scroll', scrollHandler);
  }, [ scrollContainer ]);

  useResizeObserver(body, () => {
    if (scrollContainer && sticked) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'instant',
      });
    }
  });

  useResizeObserver(scrollContainer, () => {
    if (scrollContainer && sticked) {
      scrollContainer.scrollTo({
        top: scrollContainer.scrollHeight,
        behavior: 'instant',
      });
    }
  });

  const timezone = useTimezone();

  const groups = React.useMemo(() => {
    return messages.reduce((acc, message) => {
      const date = CalendarDateService.date(message.sentAt).tz(timezone).format(DateFormats.ISO_DATE);
      const latestGroup = acc[acc.length - 1];

      if (latestGroup && latestGroup.date === date) {
        latestGroup.messages.push(message);
      } else {
        const newGroup = { date: date, messages: [ message ] };

        acc.push(newGroup);
      }

      return acc;
    }, [] as { date: string; messages: Message[] }[]);
  }, [ messages, timezone ]);

  return (
    <Box
      component="div"
      key={conversationId}
      ref={setBody}
      sx={{
        gap: 0,
        py: 2,
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {groups.map(group => (
        <MessagesGroup
          key={group.date}
          color={color}
          group={group}
        />
      ))}
      {isLoading && (
        <Loader color={color} />
      )}
    </Box>
  );
};
